import { ChevronLeftIcon } from '@heroicons/react/16/solid'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Button } from '../catalyst/button'
import { Dialog, DialogActions, DialogBody, DialogTitle } from '../catalyst/dialog'
import { Field as FormField, FieldGroup, Label } from '../catalyst/fieldset'
import { Heading } from '../catalyst/heading'
import { Input } from '../catalyst/input'
import { Link } from '../catalyst/link'
import { Select } from '../catalyst/select'
import { Text } from '../catalyst/text'
import { useAmendmentLogs, type CreateAmendmentLogInput } from '../lib/amendment-logs'
import { useAuth } from '../lib/auth'
import { useFarms } from '../lib/farms'
import { useField } from '../lib/field'
import { ComplianceBadge } from '../lib/compliance-badge'
import { Shell } from '../pages/shell'
import type { AmendmentLog } from '../lib/types'
import { canadaGapBadgeProps, draftNumber, exportH2, issueFor, useCanadaGapStatus, useEntryCheck } from '../lib/canadagap'
import { useFieldSeason } from '../lib/field-seasons'
import { checkBadgeProps, EntryCheckPanel, InputWarning, SavedEntryNotice } from './entry-check'
import { EntryIssues } from './entry-issues'
import { SeasonHeaderDialog } from './season-header-dialog'

const formatDate = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })

const SUBSTANCE_TYPES = ['poultry manure', 'cattle manure', 'hog manure', 'compost', 'biosolids', 'mulch', 'row cover', 'other']

function AmendmentLogDialog({
  open,
  onClose,
  initial,
  onSave,
}: {
  open: boolean
  onClose: () => void
  initial?: AmendmentLog
  onSave: (input: Omit<CreateAmendmentLogInput, 'fieldId' | 'seasonYear'>) => Promise<unknown>
}) {
  const [appliedOn, setAppliedOn] = useState(initial?.appliedOn ?? '')
  const [substance, setSubstance] = useState(initial?.substance ?? '')
  const [substanceType, setSubstanceType] = useState(initial?.substanceType ?? '')
  const [supplierName, setSupplierName] = useState(initial?.supplierName ?? '')
  const [rateAmount, setRateAmount] = useState(initial?.rateAmount == null ? '' : String(initial.rateAmount))
  const [rateUnit, setRateUnit] = useState(initial?.rateUnit ?? 't/ha')
  const [eahd, setEahd] = useState(initial?.eahd ?? '')
  const [applicatorName, setApplicatorName] = useState(initial?.applicatorName ?? '')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { org } = useAuth()
  const { farmId } = useParams<{ farmId: string }>()
  const check = useEntryCheck(org?.id, farmId, {
    form: 'h2Amendment',
    entry: {
      substance,
      substanceType,
      supplierName,
      rateAmount: draftNumber(rateAmount),
      rateUnit: rateAmount ? rateUnit : null,
      eahd: eahd || null,
      applicatorName,
    },
  })

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (!appliedOn || !substance.trim()) return
    setSubmitting(true)
    setError(null)
    try {
      await onSave({
        appliedOn,
        substance: substance.trim(),
        substanceType: substanceType || null,
        supplierName: supplierName.trim() || null,
        rateAmount: rateAmount ? Number(rateAmount) : null,
        rateUnit: rateAmount && rateUnit ? rateUnit : null,
        eahd: eahd || null,
        applicatorName: applicatorName.trim() || null,
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save amendment log.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{initial ? 'Edit amendment application' : 'Log an amendment application'}</DialogTitle>
      <div className="flex flex-wrap gap-1.5">
        <ComplianceBadge framework="CanadaGAP" formCode="H2" formName="Agronomic Inputs (Other)" section="Section 2 — Amendments" {...checkBadgeProps(check)} />
      </div>
      <form onSubmit={handleSubmit}>
        <DialogBody>
          {error && <Text className="mb-4 text-red-600 dark:text-red-400">{error}</Text>}
          <FieldGroup>
            <FormField>
              <Label>Date applied</Label>
              <Input type="date" value={appliedOn} onChange={(e) => setAppliedOn(e.target.value)} required autoFocus />
            </FormField>
            <FormField>
              <Label>What is applied</Label>
              <Input
                value={substance}
                onChange={(e) => setSubstance(e.target.value)}
                placeholder="e.g. Chicken Manure, Straw Mulch"
                required
              />
              <InputWarning result={check} input="substance" />
            </FormField>
            <FormField>
              <Label>Type</Label>
              <Select value={substanceType} onChange={(e) => setSubstanceType(e.target.value)}>
                <option value="">— select type —</option>
                {SUBSTANCE_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </option>
                ))}
              </Select>
              <InputWarning result={check} input="substanceType" />
            </FormField>
            <FormField>
              <Label>Supplier</Label>
              <Input value={supplierName} onChange={(e) => setSupplierName(e.target.value)} placeholder="Optional" />
              <InputWarning result={check} input="supplierName" />
            </FormField>
            <div className="flex gap-3">
              <FormField className="flex-1">
                <Label>Rate applied</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={rateAmount}
                  onChange={(e) => setRateAmount(e.target.value)}
                  placeholder="0"
                />
                <InputWarning result={check} input="rateAmount" />
              </FormField>
              <FormField className="w-32">
                <Label>Unit</Label>
                <Select value={rateUnit} onChange={(e) => setRateUnit(e.target.value)}>
                  <option value="t/ha">t/ha</option>
                  <option value="t/ac">t/ac</option>
                  <option value="kg/ha">kg/ha</option>
                  <option value="m³/ha">m³/ha</option>
                </Select>
              </FormField>
            </div>
            <FormField>
              <Label>EAHD (Earliest Allowable Harvest Date)</Label>
              <Input type="date" value={eahd} onChange={(e) => setEahd(e.target.value)} />
              <InputWarning result={check} input="eahd">
                Needed for CanadaGAP: manure, compost and by-products have a time delay before harvest.
              </InputWarning>
            </FormField>
            <FormField>
              <Label>Applicator name</Label>
              <Input value={applicatorName} onChange={(e) => setApplicatorName(e.target.value)} />
              <InputWarning result={check} input="applicatorName" />
            </FormField>
          </FieldGroup>
          <EntryCheckPanel result={check} formCode="H2" />
        </DialogBody>
        <DialogActions>
          <div className="sm:mr-auto">
            <ComplianceBadge framework="CanadaGAP" formCode="H2" formName="Agronomic Inputs (Other)" section="Section 2 — Amendments" placement="above" {...checkBadgeProps(check)} />
          </div>
          <Button plain onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export function AmendmentLogsPage() {
  const { farmId, fieldId } = useParams<{ farmId: string; fieldId: string }>()
  const { org } = useAuth()
  const { getFarm } = useFarms()
  const currentYear = new Date().getFullYear()
  const [seasonYear, setSeasonYear] = useState(currentYear)
  const [dialog, setDialog] = useState<{ log?: AmendmentLog } | null>(null)
  const [seasonOpen, setSeasonOpen] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)
  const [saved, setSaved] = useState<string | null>(null)

  const { field, loading: fieldLoading, error: fieldError } = useField(org?.id, farmId, fieldId)
  const { logs, loading: logsLoading, error: logsError, addLog, updateLog } = useAmendmentLogs(org?.id, farmId, fieldId, seasonYear)
  const { status, reload: reloadStatus } = useCanadaGapStatus(org?.id, farmId, fieldId, seasonYear)
  const { season, saveSeason } = useFieldSeason(org?.id, farmId, fieldId, seasonYear)

  const farm = farmId ? getFarm(farmId) : undefined
  const backHref = `/farms/${farmId}/fields/${fieldId}`
  const loading = fieldLoading || logsLoading
  const error = fieldError || logsError

  const years = [currentYear - 2, currentYear - 1, currentYear]

  async function handleExport() {
    if (!org?.id || !farmId || !fieldId) return
    setExporting(true)
    setExportError(null)
    try {
      await exportH2(org.id, farmId, fieldId, seasonYear)
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Export failed.')
    } finally {
      setExporting(false)
    }
  }

  async function saveLog(input: Omit<CreateAmendmentLogInput, 'fieldId' | 'seasonYear'>) {
    if (!fieldId) return
    const log = dialog?.log ? await updateLog(dialog.log.id, input) : await addLog({ fieldId, seasonYear, ...input })
    await reloadStatus()
    if (log) setSaved(log.id)
  }

  // The after-save notice is about one entry: clear it when another dialog opens or the season changes.
  useEffect(() => {
    if (dialog) setSaved(null)
  }, [dialog])
  useEffect(() => setSaved(null), [seasonYear])

  return (
    <Shell>
      <Link href={backHref} className="inline-flex items-center gap-1 text-sm/6 font-medium text-zinc-500 dark:text-zinc-400">
        <ChevronLeftIcon className="size-4" />
        {field?.name ?? farm?.name ?? 'Field'}
      </Link>

      {error ? (
        <div className="mt-6 rounded-lg border border-dashed border-red-300 p-10 text-center dark:border-red-900">
          <Text>{error}</Text>
        </div>
      ) : loading ? (
        <div className="mt-6 rounded-lg border border-dashed border-zinc-950/15 p-10 text-center dark:border-white/15">
          <Text>Loading…</Text>
        </div>
      ) : (
        <>
          <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
            <div>
              <Heading>{field?.name} — Amendment logs</Heading>
              <div className="flex flex-wrap gap-1.5">
                <ComplianceBadge framework="CanadaGAP" formCode="H2" formName="Agronomic Inputs (Other)" section="Section 2 — Amendments" {...canadaGapBadgeProps(status, 'h2Amendment')} />
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Select value={seasonYear} onChange={(e) => setSeasonYear(Number(e.target.value))} className="w-28">
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </Select>
              <Button outline onClick={() => setSeasonOpen(true)}>
                Season details
              </Button>
              <Button outline onClick={handleExport} disabled={exporting || logs.length === 0}>
                {exporting ? 'Exporting…' : 'Export H2'}
              </Button>
              <Button href={`/farms/${farmId}/log/amendment?field=${fieldId}`}>Log amendment</Button>
            </div>
          </div>

          {exportError && <Text className="mt-3 text-red-600 dark:text-red-400">{exportError}</Text>}
          {saved && status && (
            <SavedEntryNotice formCode="H2" issues={[issueFor(status.forms.h2Amendment, saved)].filter((i) => i !== undefined)} onDismiss={() => setSaved(null)} />
          )}

          {logs.length === 0 ? (
            <div className="mt-8 rounded-lg border border-dashed border-zinc-950/15 p-10 text-center dark:border-white/15">
              <Text>No amendment logs for {seasonYear}.</Text>
            </div>
          ) : (
            <ul className="mt-8 -mb-8">
              {logs.map((log, index) => (
                <li key={log.id}>
                  <div className="relative pb-8">
                    {index < logs.length - 1 && (
                      <span className="absolute left-4 top-4 -ml-px h-full w-0.5 bg-zinc-200 dark:bg-zinc-700" aria-hidden="true" />
                    )}
                    <div className="relative flex gap-4">
                      <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-zinc-100 ring-1 ring-zinc-950/10 dark:bg-zinc-800 dark:ring-white/10">
                        <span className="size-2 rounded-full bg-zinc-400 dark:bg-zinc-500" />
                      </div>
                      <button
                        type="button"
                        onClick={() => setDialog({ log })}
                        className="-m-2 min-w-0 flex-1 rounded-lg p-2 pt-2.5 text-left hover:bg-zinc-950/[2.5%] dark:hover:bg-white/[2.5%]"
                      >
                        <p className="text-sm font-semibold text-zinc-950 dark:text-white">{formatDate(log.appliedOn)}</p>
                        <p className="mt-0.5 text-sm text-zinc-700 dark:text-zinc-300">
                          {log.substance}
                          {log.substanceType && <span className="text-zinc-400"> · {log.substanceType}</span>}
                        </p>
                        <p className="mt-0.5 text-sm text-zinc-500 dark:text-zinc-400">
                          {log.rateAmount != null && log.rateUnit && <>{log.rateAmount} {log.rateUnit}</>}
                          {log.eahd && <>{log.rateAmount != null ? ' · ' : ''}EAHD {formatDate(log.eahd)}</>}
                          {log.applicatorName && <> · {log.applicatorName}</>}
                        </p>
                        <EntryIssues issue={issueFor(status?.forms.h2Amendment, log.id)} />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {dialog && (
            <AmendmentLogDialog
              key={dialog.log?.id ?? 'new'}
              open
              onClose={() => setDialog(null)}
              initial={dialog.log}
              onSave={saveLog}
            />
          )}
          <SeasonHeaderDialog
            open={seasonOpen}
            onClose={() => setSeasonOpen(false)}
            seasonYear={seasonYear}
            season={season}
            saveSeason={async (input) => {
              await saveSeason(input)
              await reloadStatus()
            }}
          />
        </>
      )}
    </Shell>
  )
}
